// Level-of-detail terrain: a quadtree of 33 x 33 height tiles, each draped with a 256 px image tile.
// Tiles are refined until a grid cell covers at most `threshold` screen pixels, so detail follows the
// camera and the whole atlas area costs about as much to draw as a small one.  Edges next to coarser
// tiles are snapped onto the coarser surface, so there are no cracks.
import * as THREE from 'three';

const N = 32, NV = N + 1, SKIRT0 = NV * NV, NVERT = NV * NV + 4 * NV, SURFACE_IDX = N * N * 6;
const edgeVertex = (e, m) => (e === 0 ? m * NV : e === 1 ? m * NV + N : e === 2 ? m : N * NV + m);   // W E N S
const DIRS = [[-1, 0], [1, 0], [0, -1], [0, 1]];             // W E N S
const CORNERS = [[0, 0, -1, -1], [N, 0, 1, -1], [0, N, -1, 1], [N, N, 1, 1]];   // [i, j, dx, dy]

const VERT = `uniform vec3 mapXf; uniform vec3 depXf;
  varying vec2 vMapUv; varying vec2 vDepUv; varying float vElev; varying vec2 vXZ;
  #include <clipping_planes_pars_vertex>
  void main(){ vMapUv = mapXf.xy + uv * mapXf.z; vDepUv = depXf.xy + uv * depXf.z; vElev = position.y; vXZ = position.xz;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * mvPosition;
    #include <clipping_planes_vertex>
  }`;
const FRAG = `uniform sampler2D map; uniform sampler2D depTex; uniform float depOn; uniform float opacity; uniform float contours;
  uniform float zc; uniform sampler2D mapTex; uniform float mapOn; uniform float mapOpacity; uniform vec4 mapBounds; uniform vec4 area;
  uniform sampler2D hydroTex; uniform float hydroOn; uniform float hydroFill; uniform vec4 hydroBounds;
  uniform sampler2D streamTex; uniform float streamOn; uniform vec4 streamBounds;
  varying vec2 vMapUv; varying vec2 vDepUv; varying float vElev; varying vec2 vXZ;
  #include <clipping_planes_pars_fragment>
  void main(){
    #include <clipping_planes_fragment>
    if (vXZ.x < area.x || vXZ.x > area.y || vXZ.y < area.z || vXZ.y > area.w) discard;
    vec3 c = texture2D(map, vMapUv).rgb;
    if (depOn > 0.5) { float d = texture2D(depTex, vDepUv).r; c = mix(c, vec3(0.22,0.53,0.90) * (0.55 + 0.45 * c), 0.75 * d); }
    if (contours > 0.5) {
      float e = (vElev + zc) / 10.0; float f = abs(fract(e - 0.5) - 0.5) / max(fwidth(e), 1e-4);
      float e5 = e / 5.0; float f5 = abs(fract(e5 - 0.5) - 0.5) / max(fwidth(e5), 1e-4);
      c = mix(c, vec3(0.93,0.80,0.58), (1.0 - min(f,1.0)) * 0.35); c = mix(c, vec3(0.97,0.86,0.64), (1.0 - min(f5/1.6,1.0)) * 0.75);
    }
    float a = opacity;
    if (hydroOn > 0.5) {                 // drainage basins: a tint inside each, solid boundaries and flow routes
      vec2 hu = vec2((vXZ.x - hydroBounds.x) / (hydroBounds.y - hydroBounds.x), 1.0 - (vXZ.y - hydroBounds.z) / (hydroBounds.w - hydroBounds.z));
      if (hu.x >= 0.0 && hu.x <= 1.0 && hu.y >= 0.0 && hu.y <= 1.0) {
        vec4 h = texture2D(hydroTex, hu);
        float line = clamp((h.a - 0.25) / 0.75, 0.0, 1.0), fill = h.a > 0.01 ? hydroFill : 0.0;   // fill is painted at alpha 0.25
        c = mix(c, h.rgb, max(line, fill));
        a = max(a, line * 0.9);
      }
    }
    if (streamOn > 0.5) {               // stream channels (Water layer), painted with their own alpha
      vec2 su = vec2((vXZ.x - streamBounds.x) / (streamBounds.y - streamBounds.x), 1.0 - (vXZ.y - streamBounds.z) / (streamBounds.w - streamBounds.z));
      if (su.x >= 0.0 && su.x <= 1.0 && su.y >= 0.0 && su.y <= 1.0) {
        vec4 w = texture2D(streamTex, su);
        c = mix(c, w.rgb, w.a); a = max(a, w.a * 0.95);
      }
    }
    if (mapOn > 0.5) {
      vec2 u = vec2((vXZ.x - mapBounds.x) / (mapBounds.y - mapBounds.x), 1.0 - (vXZ.y - mapBounds.z) / (mapBounds.w - mapBounds.z));
      float ink = (u.x < 0.0 || u.x > 1.0 || u.y < 0.0 || u.y > 1.0) ? 0.0 : texture2D(mapTex, u).r * mapOpacity;
      vec3 ic = mix(vec3(0.96,0.91,0.82), vec3(0.10,0.16,0.30), smoothstep(0.6, 0.9, opacity));
      c = mix(c, ic, ink); a = max(a, ink);
    }
    gl_FragColor = vec4(c, a);
  }`;

function blankTexture(v) {
  const t = new THREE.DataTexture(new Uint8Array([v, v, v, 255]), 1, 1); t.needsUpdate = true; return t;
}

export class Terrain {
  constructor(viewer, meta) {
    this.viewer = viewer; this.meta = meta;
    const [xc, yc, zc] = meta.origin, [rx, ryTop, S] = meta.root, [bx0, by0, bx1, by1] = meta.bbox;
    this.rootX = rx - xc; this.rootZ = -(ryTop - yc); this.S = S; this.zmax = meta.zmax;
    this.area = { x0: bx0 - xc, x1: bx1 - xc, z0: -(by1 - yc), z1: -(by0 - yc) };
    this.group = new THREE.Group();
    this.tiles = new Map(); this.textures = new Map(); this.queue = new Map();
    this.loading = 0; this.maxLoading = 8; this.texLoading = 0;
    this.threshold = 12; this.layer = null; this.depLayer = null;
    this.selected = []; this.stamp = 0; this.pending = 0;
    this.blank = blankTexture(70); this.blankDep = blankTexture(0);
    this.shared = {
      opacity: { value: 0.45 }, contours: { value: 0 }, zc: { value: zc }, depOn: { value: 0 },
      mapTex: { value: this.blankDep }, mapOn: { value: 0 }, mapOpacity: { value: 0.85 },
      mapBounds: { value: new THREE.Vector4(0, 1, 0, 1) },
      hydroTex: { value: this.blankDep }, hydroOn: { value: 0 }, hydroFill: { value: 0.3 }, hydroBounds: { value: new THREE.Vector4(0, 1, 0, 1) },
      streamTex: { value: this.blankDep }, streamOn: { value: 0 }, streamBounds: { value: new THREE.Vector4(0, 1, 0, 1) },
      area: { value: new THREE.Vector4(this.area.x0, this.area.x1, this.area.z0, this.area.z1) },
    };
    this.transparent = true;
    // every tile has the same topology: the 33 x 33 surface, then a short skirt hanging from each edge
    // (drawn only when the terrain is opaque, to hide single-pixel sparkles where levels meet)
    const uv = new Float32Array(NVERT * 2), idx = new Uint16Array(N * N * 6 + 4 * N * 6);
    for (let j = 0; j < NV; j++) for (let i = 0; i < NV; i++) { uv[2 * (j * NV + i)] = i / N; uv[2 * (j * NV + i) + 1] = 1 - j / N; }
    let k = 0;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const a = j * NV + i, b = a + 1, d = a + NV, e = d + 1;
      if ((i + j) % 2) { idx[k++] = a; idx[k++] = d; idx[k++] = b; idx[k++] = b; idx[k++] = d; idx[k++] = e; }
      else { idx[k++] = a; idx[k++] = d; idx[k++] = e; idx[k++] = a; idx[k++] = e; idx[k++] = b; }
    }
    for (let e = 0; e < 4; e++) for (let m = 0; m < NV; m++) {
      const sv = SKIRT0 + e * NV + m, v = edgeVertex(e, m);
      uv[2 * sv] = uv[2 * v]; uv[2 * sv + 1] = uv[2 * v + 1];
      if (m < N) { const v2 = edgeVertex(e, m + 1), s2 = sv + 1; idx[k++] = v; idx[k++] = sv; idx[k++] = v2; idx[k++] = v2; idx[k++] = sv; idx[k++] = s2; }
    }
    this.uv = new THREE.BufferAttribute(uv, 2); this.index = new THREE.BufferAttribute(idx, 1);
    this.frustum = new THREE.Frustum(); this.box = new THREE.Box3(); this.m4 = new THREE.Matrix4();
  }

  // ------------------------------------------------------------------ tiles
  valid(z, x, y) {
    const n = 1 << z; if (x < 0 || y < 0 || x >= n || y >= n || z > this.zmax) return false;
    const s = this.S / n;
    return this.rootX + x * s < this.area.x1 && this.rootZ + y * s < this.area.z1;
  }

  tile(z, x, y) {
    const id = `${z}/${x}/${y}`; let t = this.tiles.get(id);
    if (!t) {
      const size = this.S / (1 << z);
      t = { id, z, x, y, size, cell: size / N, x0: this.rootX + x * size, z0: this.rootZ + y * size, state: 'new', kids: null, used: 0 };
      this.tiles.set(id, t);
    }
    return t;
  }

  kids(t) {
    if (!t.kids) {
      t.kids = [];
      for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) {
        const x = 2 * t.x + dx, y = 2 * t.y + dy;
        if (this.valid(t.z + 1, x, y)) t.kids.push(this.tile(t.z + 1, x, y));
      }
    }
    return t.kids;
  }

  async start() {                          // load the root before the first frame
    const root = this.tile(0, 0, 0);
    await this.load(root);
    if (root.state !== 'ready') throw new Error('terrain tiles could not be loaded');
  }

  request(t, prio) { if (t.state === 'new' || (t.state === 'failed' && performance.now() - t.failedAt > 15000)) this.queue.set(t, Math.max(prio, this.queue.get(t) || 0)); }

  pump() {
    while (this.loading < this.maxLoading && this.queue.size) {
      let best = null, bp = -1;
      for (const [t, p] of this.queue) if (p > bp) { bp = p; best = t; }
      this.queue.delete(best);
      this.load(best);
    }
  }

  async load(t) {
    t.state = 'loading'; this.loading++;
    try {
      const r = await fetch(`/api/terrain/${this.meta.version}/${t.z}/${t.x}/${t.y}.bin`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`terrain tile ${t.id}: ${r.status}`);
      const buf = await r.arrayBuffer(), head = new DataView(buf, 0, 8);
      const lo = head.getFloat32(0, true), step = head.getFloat32(4, true), q = new Uint16Array(buf, 8, NV * NV);
      const zc = this.meta.origin[2], h = new Float32Array(NV * NV); let mn = Infinity, mx = -Infinity;
      for (let i = 0; i < NV * NV; i++) { const v = lo + q[i] * step - zc; h[i] = v; if (v < mn) mn = v; if (v > mx) mx = v; }
      t.h = h; t.hmin = mn; t.hmax = mx;
      const pos = new Float32Array(NVERT * 3);
      for (let j = 0; j < NV; j++) for (let i = 0; i < NV; i++) {
        const k = j * NV + i; pos[3 * k] = t.x0 + i * t.cell; pos[3 * k + 1] = h[k]; pos[3 * k + 2] = t.z0 + j * t.cell;
      }
      t.skirt = Math.max(0.5, t.cell * 0.4);
      this.skirts(pos, t.skirt);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('uv', this.uv); g.setIndex(this.index);
      g.setDrawRange(0, this.transparent ? SURFACE_IDX : Infinity);
      g.boundingSphere = new THREE.Sphere(new THREE.Vector3(t.x0 + t.size / 2, (mn + mx) / 2, t.z0 + t.size / 2), t.size);
      const mat = new THREE.ShaderMaterial({
        uniforms: { ...this.shared, map: { value: this.blank }, mapXf: { value: new THREE.Vector3(0, 0, 1) },
          depTex: { value: this.blankDep }, depXf: { value: new THREE.Vector3(0, 0, 1) } },
        vertexShader: VERT, fragmentShader: FRAG, side: THREE.DoubleSide,
        transparent: this.transparent, depthWrite: !this.transparent, clipping: true, clippingPlanes: this.clipPlanes || null,
      });
      t.mesh = new THREE.Mesh(g, mat); t.mesh.frustumCulled = false; t.mesh.renderOrder = 2; t.mesh.visible = false;
      t.edgeSig = '';
      if (this.layer) await this.setTileTexture(t, 'map', this.layer);
      if (this.depLayer && this.shared.depOn.value) this.setTileTexture(t, 'dep', this.depLayer);
      this.group.add(t.mesh);
      t.state = 'ready';
    } catch (e) {
      t.state = 'failed'; t.failedAt = performance.now(); console.warn(e.message);
    } finally {
      this.loading--; this.viewer.dirty = true; this.pump();
    }
  }

  // the section slicer: cut the ground with these planes too (null = whole)
  setClip(planes) {
    this.clipPlanes = planes;
    for (const t of this.tiles.values()) if (t.mesh) { t.mesh.material.clippingPlanes = planes; t.mesh.material.needsUpdate = true; }
    this.viewer.dirty = true;
  }

  skirts(p, depth) {
    for (let e = 0; e < 4; e++) for (let m = 0; m < NV; m++) {
      const v = edgeVertex(e, m), sv = SKIRT0 + e * NV + m;
      p[3 * sv] = p[3 * v]; p[3 * sv + 1] = p[3 * v + 1] - depth; p[3 * sv + 2] = p[3 * v + 2];
    }
  }

  // ------------------------------------------------------------------ image tiles
  texRef(layer, t) {                       // the image tile for t: itself, or the deepest ancestor the layer has
    const zt = Math.min(t.z, layer.zmax), k = 1 << (t.z - zt), x = t.x >> (t.z - zt), y = t.y >> (t.z - zt);
    const ix = t.x - x * k, iy = t.y - y * k;
    return { key: `${layer.key}/${layer.version}/${zt}/${x}/${y}`, xf: [ix / k, (k - 1 - iy) / k, 1 / k] };
  }

  acquire(key, ext) {
    let e = this.textures.get(key);
    if (!e) {
      e = { refs: 0, tex: null };
      e.promise = new Promise((res, rej) => {
        const img = new Image(); img.decoding = 'async';
        img.onload = () => {
          const tex = new THREE.Texture(img); tex.needsUpdate = true; tex.generateMipmaps = true;
          if (ext === 'png') tex.format = THREE.RedFormat;          // masks: one byte per pixel on the GPU
          tex.minFilter = THREE.LinearMipmapLinearFilter; tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.anisotropy = Math.min(4, this.viewer.renderer.capabilities.getMaxAnisotropy());
          e.tex = tex; res(tex);
        };
        img.onerror = () => { this.textures.delete(key); rej(new Error(`image tile ${key}`)); };
        img.src = `/api/tiles/${key}.${ext}`;
      });
      this.textures.set(key, e);
    }
    e.refs++;
    return e;
  }

  release(key) {
    const e = key && this.textures.get(key); if (!e) return;
    if (--e.refs <= 0) { e.tex?.dispose(); this.textures.delete(key); }
  }

  async setTileTexture(t, which, layer) {
    const { key, xf } = this.texRef(layer, t), slot = which === 'map' ? 'map' : 'depTex', xfSlot = which === 'map' ? 'mapXf' : 'depXf';
    const cur = which === 'map' ? 'texKey' : 'depKey', want = which === 'map' ? 'texWant' : 'depWant';
    if (t[cur] === key || t[want] === key) return;
    t[want] = key;
    const e = this.acquire(key, which === 'map' ? 'jpg' : 'png');
    this.texLoading++;
    try {
      const tex = await e.promise;
      if (t[want] !== key || !t.mesh) { this.release(key); return; }        // superseded or evicted meanwhile
      const u = t.mesh.material.uniforms; u[slot].value = tex; u[xfSlot].value.set(...xf);
      this.release(t[cur]); t[cur] = key; t[want] = null;
      this.viewer.dirty = true;
    } catch (err) {
      this.release(key); if (t[want] === key) t[want] = null;
    } finally {
      this.texLoading--;
    }
  }

  get busy() { return this.queue.size + this.loading + this.texLoading; }

  setLayer(layer) {
    if (this.layer && layer && this.layer.key === layer.key && this.layer.version === layer.version) return;
    this.layer = layer; this.refreshTextures = true; this.viewer.dirty = true;
  }
  setDepLayer(layer) {
    if (this.depLayer && layer && this.depLayer.version === layer.version) return;
    this.depLayer = layer; this.refreshTextures = true; this.viewer.dirty = true;
  }
  setDepOn(on) { this.shared.depOn.value = on ? 1 : 0; this.refreshTextures = true; this.viewer.dirty = true; }

  setOpacity(o) {
    this.shared.opacity.value = o;
    const tr = o < 0.99;
    if (tr !== this.transparent) {
      this.transparent = tr;
      for (const t of this.tiles.values()) if (t.mesh) {
        t.mesh.material.transparent = tr; t.mesh.material.depthWrite = !tr; t.mesh.material.needsUpdate = true;
        t.mesh.geometry.setDrawRange(0, tr ? SURFACE_IDX : Infinity);
      }
    }
    this.group.visible = o > 0.01 || this.shared.mapOn.value > 0;
  }

  // ------------------------------------------------------------------ per-frame selection
  update(camera, viewportHeight) {
    const ve = this.viewer.opts.ve, cam = camera.position;
    camera.updateMatrixWorld();
    this.frustum.setFromProjectionMatrix(this.m4.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    const K = viewportHeight / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
    const sel = [], stamp = ++this.stamp;
    this.queue.clear();
    const visit = (t) => {
      t.used = stamp;
      if (t.state !== 'ready') { this.request(t, 1e9); return; }
      this.box.min.set(t.x0, t.hmin * ve - 1, t.z0); this.box.max.set(t.x0 + t.size, t.hmax * ve + 1, t.z0 + t.size);
      if (t.z > 0 && !this.frustum.intersectsBox(this.box)) return;
      const sse = t.cell * K / Math.max(this.box.distanceToPoint(cam), 1e-3);
      if (t.z < this.zmax && sse > this.threshold) {
        const kids = this.kids(t);
        if (kids.every(k => k.state === 'ready')) { for (const k of kids) visit(k); return; }
        for (const k of kids) { k.used = stamp; this.request(k, sse); }
      }
      sel.push(t);
    };
    visit(this.tile(0, 0, 0));
    this.pump();
    for (const t of this.selected) if (t.mesh) t.mesh.visible = false;
    this.selected = sel;
    const byId = new Map(sel.map(t => [t.id, t]));
    sel.sort((a, b) => a.z - b.z || a.y - b.y || a.x - b.x);   // coarse first; within a level, west/north neighbours first
    for (const t of sel) {
      t.mesh.visible = true;
      this.stitch(t, byId);
      if (this.layer && (this.refreshTextures || t.texKey !== this.texRef(this.layer, t).key)) this.setTileTexture(t, 'map', this.layer);
      if (this.depLayer && this.shared.depOn.value && (this.refreshTextures || !t.depKey)) this.setTileTexture(t, 'dep', this.depLayer);
    }
    this.refreshTextures = false;
    this.evict(stamp);
    this.pending = this.busy;
  }

  covering(z, x, y, byId) {                 // the drawn tile that covers tile (z, x, y)'s area at its level or coarser
    for (let zz = z; zz >= 0; zz--) {
      const s = z - zz, t = byId.get(`${zz}/${x >> s}/${y >> s}`);
      if (t) return t;
    }
    return null;
  }

  stitch(t, byId) {
    // edge neighbours that are coarser (snap onto them), or same-level west/north ones (copy their edge exactly)
    const nb = DIRS.map(([dx, dy], d) => { const n = this.covering(t.z, t.x + dx, t.y + dy, byId);
      return n && (n.z < t.z || (n.z === t.z && (d === 0 || d === 2))) ? n : null; });
    const cn = CORNERS.map(([, , dx, dy]) => {
      let best = null;
      for (const [ox, oy] of [[dx, 0], [0, dy], [dx, dy]]) {
        const n = this.covering(t.z, t.x + ox, t.y + oy, byId);
        if (n && n.z < t.z && (!best || n.z < best.z)) best = n;
      }
      return best;
    });
    const key = (n) => n ? `${n.id}@${n.stitchVer}` : '';
    const sig = nb.map(key).join('|') + '#' + cn.map(key).join('|');
    if (sig === t.edgeSig) return;
    t.edgeSig = sig; t.stitchVer = (t.stitchVer || 0) + 1;
    const pos = t.mesh.geometry.attributes.position, p = pos.array, h = t.h;
    for (let k = 0; k < NV * NV; k++) p[3 * k + 1] = h[k];
    const setV = (i, j, n) => { const k = j * NV + i; p[3 * k + 1] = this.sampleTile(n, p[3 * k], p[3 * k + 2], true); };
    for (let m = 0; m < NV; m++) {
      if (nb[0]) setV(0, m, nb[0]); if (nb[1]) setV(N, m, nb[1]);
      if (nb[2]) setV(m, 0, nb[2]); if (nb[3]) setV(m, N, nb[3]);
    }
    CORNERS.forEach(([i, j], c) => { if (cn[c]) setV(i, j, cn[c]); });
    this.skirts(p, t.skirt);
    pos.needsUpdate = true;
  }

  sampleTile(t, x, z, drawn = false) {     // bilinear height (local units) inside a loaded tile
    const u = Math.min(N, Math.max(0, (x - t.x0) / t.cell)), w = Math.min(N, Math.max(0, (z - t.z0) / t.cell));
    const i = Math.min(N - 1, Math.floor(u)), j = Math.min(N - 1, Math.floor(w)), fu = u - i, fw = w - j, k = j * NV + i;
    const at = drawn && t.mesh ? ((p) => (m) => p[3 * m + 1])(t.mesh.geometry.attributes.position.array) : (m) => t.h[m];
    return at(k) * (1 - fu) * (1 - fw) + at(k + 1) * fu * (1 - fw) + at(k + NV) * (1 - fu) * fw + at(k + NV + 1) * fu * fw;
  }

  heightAt(x, z) {                         // finest loaded height at a local position
    let t = this.tiles.get('0/0/0'); if (!t || t.state !== 'ready') return 0;
    for (;;) {
      if (!t.kids) break;
      const half = t.size / 2, cx = x >= t.x0 + half ? 1 : 0, cy = z >= t.z0 + half ? 1 : 0;
      const k = t.kids.find(c => c.x === 2 * t.x + cx && c.y === 2 * t.y + cy);
      if (!k || k.state !== 'ready') break;
      t = k;
    }
    return this.sampleTile(t, x, z);
  }

  evict(stamp) {                           // keep GPU memory bounded: drop tiles not drawn or visited lately
    const ready = [...this.tiles.values()].filter(t => t.state === 'ready');
    if (ready.length < 500) return;
    const old = ready.filter(t => t.z > 2 && t.used < stamp).sort((a, b) => a.used - b.used || b.z - a.z);   // least recently used
    for (const t of old.slice(0, ready.length - 400)) if (t.state === 'ready') this.unload(t);
  }

  unload(t) {
    if (t.mesh) { this.group.remove(t.mesh); t.mesh.geometry.dispose(); t.mesh.material.dispose(); t.mesh = null; }
    this.release(t.texKey); this.release(t.depKey); t.texKey = t.depKey = t.texWant = t.depWant = null;
    t.h = null; t.state = 'new';
    if (t.kids) for (const k of t.kids) if (k.state === 'ready') this.unload(k);
  }

  stats() {
    let tris = 0; for (const t of this.selected) tris += N * N * 2;
    return { drawn: this.selected.length, tris, cached: [...this.tiles.values()].filter(t => t.state === 'ready').length,
      textures: this.textures.size, pending: this.busy, deepest: Math.max(0, ...this.selected.map(t => t.z)) };
  }

  dispose() {
    for (const t of this.tiles.values()) if (t.mesh) { t.mesh.geometry.dispose(); t.mesh.material.dispose(); }
    for (const e of this.textures.values()) e.tex?.dispose();
    this.tiles.clear(); this.textures.clear(); this.group.removeFromParent();
  }
}
